import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    readLocationsIP,
    saveLocationIP,
} from '../helpers'

interface LocationsIpFormShape {
    name: string
    addressPrefix: string
    [key: string]: unknown
}

interface LocationIPEntity {
    addressPrefix: string
}

interface LocationsIpFormProps {
    locationsIP: LocationIPEntity[]
    successSaveFunction?: () => void
}

const PREFIX_OCTET_MIN = 1
const PREFIX_OCTET_MAX = 255
const PREFIX_NETWORK = '10.8.'

/**
 * Form for creating a new locationIP entry restricted to the
 * `10.8.X` network range with an octet between 1 and 255.
 */
const LocationsIpForm = ({ locationsIP, successSaveFunction }: LocationsIpFormProps) => {
    const { t } = useTranslation()

    const [form, setForm] = useState<LocationsIpFormShape>({
        name: '',
        addressPrefix: '',
    })
    const [addressPrefixError, setAddressPrefixError] = useState<string | null>(null)

    const updateForm = <K extends keyof LocationsIpFormShape>(
        key: K,
        value: LocationsIpFormShape[K],
    ): void => {
        setForm(previous => ({
            ...previous,
            [key]: value,
        }))
    }

    const validForm = (): boolean => {
        if (!form.addressPrefix || form.addressPrefix === '') {
            setAddressPrefixError(t('device.locationIP.prefixMustNotBeEmpty'))
            return false
        }

        const parts = form.addressPrefix.split('.')
        const lastNumber = Number(parts[2])

        const invalidShape = form.addressPrefix.substring(0, 5) !== PREFIX_NETWORK
            || Number.isNaN(lastNumber)
            || lastNumber < PREFIX_OCTET_MIN
            || lastNumber > PREFIX_OCTET_MAX
            || parts.length !== 3

        if (invalidShape) {
            setAddressPrefixError(t('device.locationIP.prefixInvalidFormat'))
            return false
        }

        if (locationsIP.some(locationIP => locationIP.addressPrefix === form.addressPrefix)) {
            setAddressPrefixError(t('device.locationIP.prefixMustBeUnique'))
            return false
        }

        return true
    }

    const submitForm = (): void => {
        if (!validForm()) {
            window.swalert(t('device.locationIP.formInvalid'), 'danger')
            return
        }

        saveLocationIP(form)
            .then(() => {
                if (typeof successSaveFunction === 'function') {
                    successSaveFunction()
                }

                readLocationsIP()
            })
    }

    return (
        <div className="d-flex flex-column p-3">
            <div className="form-group">
                <label htmlFor="location-ip-name">
                    {t('device.locationIP.name')}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="location-ip-name"
                    placeholder={t('device.locationIP.name')}
                    value={form.name}
                    onChange={event => updateForm('name', event.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="location-ip-prefix">
                    {t('device.locationIP.prefix')}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="location-ip-prefix"
                    placeholder={t('device.locationIP.prefix')}
                    value={form.addressPrefix}
                    onChange={event => updateForm('addressPrefix', event.target.value)}
                />
                {addressPrefixError && <div className="text-danger">{addressPrefixError}</div>}
            </div>

            <div className="form-group row">
                <div className="col-sm-10">
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={submitForm}
                    >
                        {t('device.locationIP.save')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default LocationsIpForm
